import qc from 'qc'
import { scripts } from '@ps/bridge/qc/scripts/index'
import gameIframe from '@ps/bridge/iframe/iframe'
import router from '@ps/router'

// 自定义事件类型
type CustomEvent = {
  // 自定义事件唯一标识，设计师在自定义事件命名时填写的小驼峰英文
  id: string
  // 自定义事件描述信息，告知研发该事件是什么时机触发
  desc: string
  label: {
    // 不同语言下的展示文案
    [key: string]: string
  }
}

// 自定义事件映射表
type GameConfigMap = {
  // 记录自定义事件映射关系
  // 如果复制场景时，会改动这个 value 值，以达到 gameConfig 独立配置的目的
  [key: string]: string
}

export enum SceneType {
  // 普通场景
  NORMAL = 0,
  // 全局场景
  GLOBAL = 1,
  // 玩法场景
  PLAY = 2,
}

export enum GlobalLevel {
  // 全局配置场景的层级，0 代表底部（默认值）
  BOTTOM = 0,
  // 1 代表顶部
  TOP = 1,
}

// 部分编辑器层面使用的扩展字段
type editorData = {
  [key: string]: any
}

export interface SceneConfigScript {
  isEnding?: boolean
  customEventSet?: CustomEvent[]
  gameConfigMap?: GameConfigMap
  sceneType?: SceneType
  playUUID?: string
  isShowGlobal?: boolean
  globalLevel?: GlobalLevel
  $data?: editorData
}

export const defaultSceneConfig: SceneConfigScript = {
  isEnding: false,
  customEventSet: [],
  gameConfigMap: {},
  sceneType: SceneType.NORMAL,
  playUUID: '',
  isShowGlobal: true,
  globalLevel: GlobalLevel.TOP,
  $data: {},
}

/**
 * ps.SceneConfigScript 脚本在编辑与线上运行的时候都会加载进去
 * @param node
 * @param addScriptIfScriptNotExist
 */
export const getScript = (
  node: qc.Node,
  addScriptIfScriptNotExist = true,
): SceneConfigScript | undefined => {
  const config: SceneConfigScript = node.getScript(scripts.sceneConfig.name)
  if (config) {
    return config
  }
  if (addScriptIfScriptNotExist) {
    return node.addScript(scripts.sceneConfig.name) as SceneConfigScript
  }
}

/**
 * 判断场景脚本是否包含指定特性
 * @returns {boolean}
 */
export const checkSceneIsSupportProp = (prop: string): boolean => {
  const supportAbility = gameIframe.ps.SceneConfig.__ability || ''
  return supportAbility.indexOf(prop) !== -1
}

export const setSceneConfig = (
  node: qc.Node,
  config: SceneConfigScript,
): SceneConfigScript => {
  let sceneConfig = getScript(node) as SceneConfigScript
  sceneConfig = Object.assign(sceneConfig, config)
  return sceneConfig
}

// 是否是普通场景
export const isNormalScene = (node: qc.Node): boolean => {
  const SceneConfig = node && getScript(node, false)
  return Boolean(
    SceneConfig &&
      (SceneConfig.sceneType === SceneType.NORMAL ||
        typeof SceneConfig.sceneType === 'undefined'),
  )
}

// 是否是全局场景
export const isGlobalScene = (node: qc.Node): boolean => {
  const SceneConfig = node && getScript(node, false)
  return Boolean(SceneConfig && SceneConfig.sceneType === SceneType.GLOBAL)
}

// 是否玩法场景
export const isPlayScene = (node: qc.Node): boolean => {
  const SceneConfig = node && getScript(node, false)
  return Boolean(SceneConfig && SceneConfig.sceneType === SceneType.PLAY)
}

// 通过普通场景获取新增场景名字
export const getSceneUniName = (
  sceneList: [qc.Node],
  type: SceneType,
): string => {
  // 根据普通场景列表获取场景数
  const filterScene = sceneList.filter((scene) => {
    if (type === SceneType.PLAY) {
      return isPlayScene(scene)
    }
    return isNormalScene(scene)
  })
  const names = filterScene.map((item) => item.uniqueName)
  let num = names.length + 1
  const tempStr = type === SceneType.PLAY ? `Play_Scene_` : `New_Scene_`
  let tempName = `${tempStr}${num}`
  while (names.some((item) => item === tempName)) {
    num = num + 1
    tempName = `${tempStr}${num}`
  }
  return tempName
}
// 是否粒子编辑页面
export const isParticleEditPage = () => {
  return router.currentRoute.query.asset_type === 'particle'
}
